import {
  onBeforeMount,
  getCurrentScope,
  computed,
} from 'vue'
import { useRefreshToken } from '@services/api/useRefreshToken/useRefreshToken'
import { handleAuthRedirects } from '@core/router/handleAuthRedirects'
import { isRefreshTokenForbidden } from '@services/api/useRefreshToken/forbiddenScenarios'
import { devLogger } from '@libs/fsc/utils/logger'
import { handleWorkerCheckTokenExpiration } from '@core/workers/auth/authWorkerHandlers'
import { createAuthWorker } from '@core/workers/auth/authWorkerClient'
import { useCluberLocalStorage } from '@cluber/tools-composables'
import { LocalStorageAuth } from '@services/Auth/declarations'
import { axiosInstance } from '@services/api/apiClient'
import { UserProfile } from '@services/User/declarations'

const { storage } = useCluberLocalStorage<LocalStorageAuth>({
  auth: {
    token: '',
    userIdentifier: '',
    refreshTokenValidity: undefined,
    tokenTtl: undefined,
  },
  currentProfile: undefined,
}, {
  initOnMounted: true,
  mergeDefaults: true,
})

// ***
// DEFAULTS
// ***

export function useUserAuth() {
  const {
    startRefreshTokenProcess,
    legacyRefreshTokenIsAboutToExpire,
    calculateTokenValidity,
    startWorkerTokenExpirationCheck,
  } = useRefreshToken()

  // ***
  // REFRESH TOKEN PROCESS
  // ***

  async function handleRefreshTokenRequestProcess(isRefreshForbidden?: boolean) {
    const isForbidden = isRefreshForbidden ?? isRefreshTokenForbidden()

    if (legacyRefreshTokenIsAboutToExpire() && !isForbidden) {
      try {
        await startRefreshTokenProcess()
      } catch (error) {
        if (error instanceof Error && error.message === 'userDeniedRefreshToken') {
          handleAuthRedirects()
        } else {
          devLogger('[Client] error during refresh token process', 'error')
          window.location.pathname = '/auth/logout'
        }
      }
    }
  }

  function startRefreshTokenProcessWithAuthWorker() {
    const runner = () => {
      createAuthWorker()
      handleWorkerCheckTokenExpiration()
      startWorkerTokenExpirationCheck()
    }

    if (getCurrentScope()) {
      onBeforeMount(() => {
        runner()
      })
    } else {
      runner()
    }
  }

  // ***
  // USER LOGIN
  // ***
  function initUserAuthData({
    tokenTtl,
    userIdentifier,
    token,
  } : {
    tokenTtl : number,
    userIdentifier: string,
    token: string,
  }) {
    Object.assign(storage.value, {
      auth: {
        token,
        userIdentifier,
        refreshTokenValidity: calculateTokenValidity(tokenTtl),
        tokenTtl,
      },
      xAuthTokenApplied: false,
    })
    axiosInstance.defaults.headers.common.Authorization = `Bearer ${token}`
  }

  function setUserProfile(profile: Partial<UserProfile>) {
    Object.assign(storage.value, {
      currentProfile: profile,
    })
  }

  function getActiveProfile() {
    return computed(() => storage.value.currentProfile)
  }

  return {
    handleRefreshTokenRequestProcess,
    startRefreshTokenProcessWithAuthWorker,
    initUserAuthData,
    setUserProfile,
    getActiveProfile,
  }
}
